import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { routeLabel } from "../../lib/trip-route";
import { DayCityPicker } from "./day-city-picker";
import { StayPickerDialog, TripStayCard } from "./trip-stay-card";
import { useTripStayPrices } from "../../hooks/use-trip-stays";
import { TripStopMenu } from "./trip-stop-menu";
import { TripStopTimeEditor } from "./trip-stop-time-editor";
import { TripMapBackdrop } from "./trip-map-backdrop";
import { TripTotalsCard } from "./trip-totals-card";
import { tripStopCount } from "./trip-metrics";
import type { Trip, TripDay } from "../../types";
import { useTripActions } from "../../state/trip-context";
import { useDiscoverActions, useDiscoverCities, useDiscoverItems } from "../../state/discover-context";
import { useDiscoverConfig } from "../../config";
import { defaultRailHref } from "../discover-rail";
import { dayDate, formatDayLabel, tripStartOf } from "./trip-date";
import { AddStopDialog } from "./add-stop-dialog";
import { StopPrice } from "./stop-price";
import { TripDayBooking } from "./trip-day-booking";
import { TripWhenDialog } from "./trip-when-dialog";
import { useStopOffers } from "../../hooks/use-trip-booking";
import { tripActivitySlugs, type OfferLookup } from "../../lib/trip-booking";

import { Icon } from "@majourneys/icons";
interface TripDetailProps {
  readonly trip: Trip;
}

const ALL_DAYS = "__all__";

/**
 * One trip, open.
 *
 * This is the phone's trip screen, on the web (owner, 2026-09-18). The two
 * had drifted into different products at the same address: the web grew tabs
 * (Itinerary / Suggestions / Calendar), a Days-vs-Timeline toggle, a saved
 * column and an airport-transfer switch that the app never had, while the app
 * had a full-screen map with day chips that the web never had. Same trip, two
 * shapes, and every fix had to be made twice.
 *
 * So the shape is the app's, exactly: a header carrying the name and the
 * one-line count, a List/Map toggle, the plan bar, the days, the totals. The
 * five web-only pieces were deliberately dropped rather than parked somewhere
 * — the owner chose a strict match over keeping them (2026-09-18).
 *
 * **The map is a peer view, not a widget.** The app made it a whole screen
 * because a map you can only see three centimetres of is decoration. The web
 * used to inline a small one inside each day; here it takes the pane, with
 * the day chips filtering it.
 */
export function TripDetail({ trip }: TripDetailProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.slice(0, 2) || "en";
  const [view, setView] = useState<"list" | "map">("list");
  const [dayTab, setDayTab] = useState<string>(ALL_DAYS);
  const [addStopDayId, setAddStopDayId] = useState<string | null>(null);
  const items = useDiscoverItems();
  const cities = useDiscoverCities();
  const { railHref } = useDiscoverConfig();

  const {
    setActiveTrip,
    deleteTrip,
    addDay,
    removeDay,
    removeStop,
    moveStop,
    updateStopTime,
    updateStopNote,
    sortDayByTime,
    renameTrip,
    reorderStops,
  } = useTripActions();

  // Inline title editing. Click the trip title to swap it for an input;
  // Enter commits, Escape cancels.
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState(trip.name);

  const { selectItem, setUtilityMode } = useDiscoverActions();
  const resolveItem = useMemo(() => {
    const itemMap = new Map(items.map((item) => [item.id, item]));
    return (id: string) => itemMap.get(id) ?? null;
  }, [items]);

  const stopCount = tripStopCount(trip);
  // One request per activity across the trip, shared by every day and the
  // totals, so a day and the total under it can never disagree.
  const slugs = useMemo(() => tripActivitySlugs(trip, resolveItem), [trip, resolveItem]);
  const offerFor = useStopOffers(slugs);
  const [whenOpen, setWhenOpen] = useState(false);
  /** The city whose stay the picker is choosing, by its first day. */
  const [stayFor, setStayFor] = useState<string | null>(null);
  const stays = useTripStayPrices(trip);
  const cityLabel = routeLabel(
    trip,
    (slug) => cities.find((c) => c.slug === slug)?.name ?? slug.charAt(0).toUpperCase() + slug.slice(1),
    i18n.dir(i18n.language) === "rtl",
  );

  /**
   * The header's one line, the way the phone writes it: city, days, stops,
   * joined by a middle dot, with anything missing simply absent rather than
   * leaving a stray separator.
   */
  const metaLine = [
    cityLabel,
    t("trip.day_count", { count: trip.days.length }),
    t("trip.stop_count", { count: stopCount }),
  ]
    .filter(Boolean)
    .join(" · ");

  // Map view: every stop, or one day's, depending on the chips.
  const mapStops = useMemo(() => {
    if (dayTab === ALL_DAYS) return trip.days.flatMap((d) => d.stops);
    return trip.days.find((d) => d.id === dayTab)?.stops ?? [];
  }, [trip.days, dayTab]);

  const handleDelete = () => {
    if (confirm(t("trip.confirm_delete", { name: trip.name })) === true) {
      deleteTrip(trip.id);
    }
  };

  const planHref = (railHref ?? defaultRailHref)("chat", lang);

  return (
    <div className="mj-trip-glass-shell mj-trip-shell--detail">
      <div className="mj-trip-app" data-view={view}>
        {/* One header for both views, so switching does not resize the box
            the views sit in. */}
        <header className="mj-trip-app__head">
          <button
            type="button"
            className="mj-trip-app__back"
            onClick={() => setActiveTrip(null)}
            aria-label={t("trip.back_to_list")}
          >
            <Icon name="ri-arrow-left-line" className="rtl:-scale-x-100" aria-hidden="true" />
          </button>

          <div className="mj-trip-app__ident">
            {titleEditing ? (
              <input
                type="text"
                className="mj-trip-app__title mj-trip-app__title--editing"
                autoFocus
                value={titleDraft}
                maxLength={60}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => {
                  const trimmed = titleDraft.trim();
                  if (trimmed && trimmed !== trip.name) {
                    renameTrip(trip.id, trimmed);
                  } else {
                    setTitleDraft(trip.name);
                  }
                  setTitleEditing(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") e.currentTarget.blur();
                  if (e.key === "Escape") {
                    setTitleDraft(trip.name);
                    setTitleEditing(false);
                  }
                }}
                aria-label={t("trip.trip_name")}
              />
            ) : (
              <button
                type="button"
                className="mj-trip-app__title mj-trip-app__title--button"
                title={t("trip.click_to_rename", { name: trip.name })}
                onClick={() => {
                  setTitleDraft(trip.name);
                  setTitleEditing(true);
                }}
              >
                {trip.name}
                <Icon name="ri-pencil-line" aria-hidden="true" />
              </button>
            )}
            <p className="mj-trip-app__meta">{metaLine}</p>
          </div>

          <button
            type="button"
            className="mj-trip-app__delete"
            onClick={handleDelete}
            aria-label={t("trip.delete_trip")}
          >
            <Icon name="ri-delete-bin-line" aria-hidden="true" />
          </button>

          {/* List / Map, for narrow screens only.
              A phone can show one pane at a time, so the app makes them peer
              views behind a toggle. A desktop has the width to show both at
              once, and a toggle that hides half a screen you already have room
              for is a control with nothing to do — so CSS hides this above
              1000px and both panes render side by side (owner, 2026-09-18). */}
          <div className="mj-trip-app__views" role="tablist" aria-label={t("trip.itinerary_view")}>
            {(["list", "map"] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                className={`mj-trip-app__view${view === v ? " is-active" : ""}`}
                onClick={() => setView(v)}
                data-testid={`trip-view-${v}`}
              >
                <Icon name={v === "list" ? "ri-list-check-2" : "ri-map-2-line"} aria-hidden="true" />
                {v === "list" ? t("trip.view_list") : t("trip.view_map")}
              </button>
            ))}
          </div>
        </header>

        {/* The page's primary action, on its own line above the scroller —
            the same place and the same weight the phone gives it. */}
        <a className="mj-trip-app__plan" href={planHref}>
          <Icon name="ri-sparkling-2-fill" aria-hidden="true" />
          {t("trip.ai.plan_bar_title")}
        </a>

        <div className="mj-trip-app__list">
            {trip.days.map((day, dayIndex) => {
              const prevDay = trip.days[dayIndex - 1];
              const nextDay = trip.days[dayIndex + 1];
              const legAt = stays.legs.findIndex((l) => l.dayIds[0] === day.id);
              const leg = stays.legs[legAt];
              return (
                <Fragment key={day.id}>
                {leg && leg.nights > 0 ? (
                  <TripStayCard
                    trip={trip}
                    leg={leg}
                    price={stays.prices[legAt]!}
                    editable
                    onChoose={() => setStayFor(day.id)}
                    onOpenVenue={(itemId) => {
                      setUtilityMode("explore");
                      selectItem(itemId);
                    }}
                  />
                ) : null}
                <ItineraryDay
                  day={day}
                  dayIndex={dayIndex}
                  trip={trip}
                  resolveItem={resolveItem}
                  onAddStop={() => setAddStopDayId(day.id)}
                  onRemoveDay={() => trip.days.length > 1 && removeDay(trip.id, day.id)}
                  onRemoveStop={(stopId) => removeStop(trip.id, day.id, stopId)}
                  onViewStop={(itemId) => {
                    setUtilityMode("explore");
                    selectItem(itemId);
                  }}
                  canRemoveDay={trip.days.length > 1}
                  onMoveStopEarlier={
                    prevDay
                      ? (stopId) => moveStop(trip.id, day.id, prevDay.id, stopId)
                      : undefined
                  }
                  onMoveStopLater={
                    nextDay
                      ? (stopId) => moveStop(trip.id, day.id, nextDay.id, stopId)
                      : undefined
                  }
                  onUpdateStopTime={(stopId, startTime, endTime) =>
                    updateStopTime(trip.id, day.id, stopId, startTime, endTime)
                  }
                  onEditStopNote={(stopId, existing) => {
                    const next = prompt(
                      existing ? t("trip.edit_note") : t("trip.add_note"),
                      existing ?? "",
                    );
                    if (next === null) return; // cancelled
                    const trimmed = next.trim();
                    updateStopNote(
                      trip.id,
                      day.id,
                      stopId,
                      trimmed.length > 0 ? trimmed : undefined,
                    );
                  }}
                  onSortDay={() => sortDayByTime(trip.id, day.id)}
                  onReorderStops={(stopIds) => reorderStops(trip.id, day.id, stopIds)}
                  offerFor={offerFor}
                  onChangeWhen={() => setWhenOpen(true)}
                />
                </Fragment>
              );
            })}
            {(() => {
              const leg = stayFor ? stays.legs.find((l) => l.dayIds[0] === stayFor) : null;
              return leg ? <StayPickerDialog trip={trip} leg={leg} onClose={() => setStayFor(null)} /> : null;
            })()}

            <div className="mj-trip-itinerary__add-day-row">
              <button
                type="button"
                className="mj-trip-itinerary__add-day"
                onClick={() =>
                  addDay(trip.id, t("trip.day_label", { number: trip.days.length + 1 }))
                }
              >
                <Icon name="ri-calendar-event-line" aria-hidden="true" />
                {t("trip.add_another_day")}
              </button>
            </div>

          {/* Totals close the page rather than open it. */}
          {stopCount > 0 ? (
            <TripTotalsCard trip={trip} resolveItem={resolveItem} offerFor={offerFor} stays={stays} />
          ) : null}
        </div>

        <div className="mj-trip-app__map">
            {/* Day chips earn their row only on the map, which shows one
                thing at a time. */}
            {trip.days.length > 1 ? (
              <div className="mj-trip-app__daytabs" role="tablist" aria-label={t("trip.day_map")}>
                <button
                  type="button"
                  role="tab"
                  aria-selected={dayTab === ALL_DAYS}
                  className={`mj-trip-app__daytab${dayTab === ALL_DAYS ? " is-active" : ""}`}
                  onClick={() => setDayTab(ALL_DAYS)}
                >
                  {t("trip.timeline.all_days")}
                </button>
                {trip.days.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    role="tab"
                    aria-selected={dayTab === d.id}
                    className={`mj-trip-app__daytab${dayTab === d.id ? " is-active" : ""}`}
                    onClick={() => setDayTab(d.id)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            ) : null}
            {mapStops.length > 0 ? (
              /* The real map, numbered pins on the stops' own coordinates. The
                 old embed dropped Google's red pin on the centre and guessed
                 where the numbers went, so pins landed in the sea (owner,
                 2026-10-11). */
              <TripMapBackdrop stops={mapStops} resolveItem={resolveItem} variant="pane" />
            ) : (
            <div className="mj-trip-pane-empty">
              <Icon name="ri-map-pin-line" aria-hidden="true" />
              <p>{t("trip.card_no_stops")}</p>
            </div>
          )}
        </div>
      </div>

      {addStopDayId && (
        <AddStopDialog
          trip={trip}
          dayId={addStopDayId}
          onClose={() => setAddStopDayId(null)}
        />
      )}
      {whenOpen && <TripWhenDialog trip={trip} onClose={() => setWhenOpen(false)} />}
    </div>
  );
}

interface ItineraryDayProps {
  readonly day: TripDay;
  readonly dayIndex: number;
  readonly trip: Trip;
  readonly resolveItem: (itemId: string) => ReturnType<typeof useDiscoverItems>[number] | null;
  readonly onAddStop: () => void;
  readonly onRemoveDay: () => void;
  readonly onRemoveStop: (stopId: string) => void;
  readonly onViewStop: (itemId: string) => void;
  readonly canRemoveDay: boolean;
  readonly onMoveStopEarlier?: (stopId: string) => void;
  readonly onMoveStopLater?: (stopId: string) => void;
  readonly onUpdateStopTime: (
    stopId: string,
    startTime: string | undefined,
    endTime: string | undefined,
  ) => void;
  readonly onEditStopNote: (stopId: string, existing: string | undefined) => void;
  readonly onSortDay: () => void;
  readonly onReorderStops: (stopIds: readonly string[]) => void;
  readonly offerFor: OfferLookup | undefined;
  readonly onChangeWhen: () => void;
}

function ItineraryDay({
  day,
  dayIndex,
  trip,
  resolveItem,
  onAddStop,
  onRemoveDay,
  onRemoveStop,
  onViewStop,
  canRemoveDay,
  onMoveStopEarlier,
  onMoveStopLater,
  onUpdateStopTime,
  onEditStopNote,
  onSortDay,
  onReorderStops,
  offerFor,
  onChangeWhen,
}: ItineraryDayProps) {
  const { t, i18n } = useTranslation();
  const dateLabel = formatDayLabel(tripStartOf(trip), dayIndex, i18n.language);
  const [collapsed, setCollapsed] = useState(false);
  const stopCount = day.stops.length;

  // Drag-to-reorder state. dragIndex = the stop currently being
  // dragged; dropIndex = the slot it will land in if released now.
  // Both reset on drop or dragend.
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  const handleDragStart = (index: number) => (e: React.DragEvent) => {
    setDragIndex(index);
    setDropIndex(index);
    e.dataTransfer.effectAllowed = "move";
    // Safari requires setData or the drag is cancelled silently.
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    if (dragIndex === null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dropIndex !== index) setDropIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const handleDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) {
      handleDragEnd();
      return;
    }
    const ids = day.stops.map((s) => s.id);
    const moved = ids.splice(dragIndex, 1)[0];
    if (moved) ids.splice(index, 0, moved);
    onReorderStops(ids);
    handleDragEnd();
  };

  return (
    <article
      className={`mj-trip-day${collapsed ? " is-collapsed" : ""}`}
      data-day-index={dayIndex}
    >
      <header className="mj-trip-day__head">
        <button
          type="button"
          className="mj-trip-day__toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={`mj-trip-day-body-${day.id}`}
          aria-label={
            collapsed
              ? t("trip.expand_day", { day: day.label })
              : t("trip.collapse_day", { day: day.label })
          }
        >
          <Icon name={collapsed ? "ri-arrow-right-s-line" : "ri-arrow-down-s-line"} className={collapsed ? "rtl:-scale-x-100" : undefined} aria-hidden="true" />
        </button>
        <div className="mj-trip-day__label">
          <span className="mj-trip-day__num">{dayIndex + 1}</span>
          <div>
            <div className="mj-trip-day__name">{day.label}</div>
            <div className="mj-trip-day__date">
              <Icon name="ri-calendar-line" aria-hidden="true" />
              {dateLabel}
              <span className="mj-trip-day__count">
                · {t("trip.stop_count", { count: stopCount })}
              </span>
            </div>
            <div className="mj-trip-day__city">
              <DayCityPicker trip={trip} day={day} dayIndex={dayIndex} />
            </div>
          </div>
        </div>
        <div className="mj-trip-day__actions">
          {day.stops.length > 1 && (
            <button
              type="button"
              className="mj-trip-day__sort"
              onClick={onSortDay}
              title={t("trip.sort_by_time_title")}
              aria-label={t("trip.sort_by_time")}
            >
              <Icon name="ri-sort-asc" aria-hidden="true" />
              <span className="mj-trip-day__sort-label">{t("trip.sort_by_time")}</span>
            </button>
          )}
          <button
            type="button"
            className="mj-trip-day__add-stop"
            onClick={onAddStop}
            aria-label={t("trip.add_stop")}
          >
            <Icon name="ri-add-line" aria-hidden="true" />
            <span className="mj-trip-day__add-stop-label">{t("trip.add_stop")}</span>
          </button>
          {canRemoveDay && (
            <button
              type="button"
              className="mj-trip-day__remove"
              onClick={onRemoveDay}
              aria-label={t("trip.remove_day", { day: day.label })}
            >
              <Icon name="ri-close-line" aria-hidden="true" />
            </button>
          )}
        </div>
      </header>

      <div
        id={`mj-trip-day-body-${day.id}`}
        className="mj-trip-day__timeline"
        hidden={collapsed}
      >
        {day.stops.map((stop, stopIndex) => {
          const item = resolveItem(stop.itemId);
          // A stop whose row is not in the loaded index still belongs to the
          // trip: it was saved with a snapshot of its title, kind and photo
          // for exactly this. Dropping it left the day header counting three
          // stops above a list of none — the timeline view has always fallen
          // back this way, and the days view had not.
          const title = item?.title ?? stop.snapshot?.title ?? null;
          if (!title) return null;
          const image = item?.image ?? stop.snapshot?.image ?? null;
          const kind = item?.subtitle ?? stop.snapshot?.kind ?? null;
          const isDragging = dragIndex === stopIndex;
          const isDropTarget =
            dragIndex !== null &&
            dropIndex === stopIndex &&
            dragIndex !== stopIndex;
          return (
            <div
              key={stop.id}
              className={`mj-trip-tstop${isDragging ? " is-dragging" : ""}${
                isDropTarget ? " is-drop-target" : ""
              }`}
              draggable
              onDragStart={handleDragStart(stopIndex)}
              onDragOver={handleDragOver(stopIndex)}
              onDrop={handleDrop(stopIndex)}
              onDragEnd={handleDragEnd}
            >
              <span
                className="mj-trip-tstop__drag-handle"
                aria-hidden="true"
                title={t("trip.drag_to_reorder")}
              >
                <Icon name="ri-draggable" />
              </span>
              <span className="mj-trip-tstop__pin" aria-hidden="true">
                <Icon name="ri-map-pin-line" />
              </span>
              <span
                className="mj-trip-tstop__thumb"
                style={image ? { backgroundImage: `url(${image})` } : undefined}
                aria-hidden="true"
              />
              <div className="mj-trip-tstop__body">
                <strong>{title}</strong>
                <div className="mj-trip-tstop__meta">
                  <TripStopTimeEditor
                    stopIndex={stopIndex}
                    startTime={stop.startTime}
                    endTime={stop.endTime}
                    onSave={(start, end) =>
                      onUpdateStopTime(stop.id, start, end)
                    }
                  />
                  {kind ? <span className="mj-trip-tstop__badge">{kind}</span> : null}
                  <StopPrice item={item} />
                </div>
                {stop.note && (
                  <p className="mj-trip-tstop__note">
                    <Icon name="ri-sticky-note-line" aria-hidden="true" />
                    {stop.note}
                  </p>
                )}
              </div>
              {item ? (
              <TripStopMenu
                item={item}
                canMoveEarlier={!!onMoveStopEarlier}
                canMoveLater={!!onMoveStopLater}
                hasNote={!!stop.note}
                onView={() => onViewStop(item.id)}
                onEditNote={() => onEditStopNote(stop.id, stop.note)}
                onMoveEarlier={() => onMoveStopEarlier?.(stop.id)}
                onMoveLater={() => onMoveStopLater?.(stop.id)}
                onRemove={() => onRemoveStop(stop.id)}
              />
              ) : (
                /* No catalogue row behind it: there is nothing to open and no
                   note to attach, so the only honest action is removing it. */
                <button
                  type="button"
                  className="mj-trip-tstop__remove"
                  onClick={() => onRemoveStop(stop.id)}
                  aria-label={t("trip.remove_stop", { defaultValue: "Remove stop" })}
                >
                  <Icon name="ri-close-line" aria-hidden="true" />
                </button>
              )}
            </div>
          );
        })}
        <button
          type="button"
          className="mj-trip-day__add-more"
          onClick={onAddStop}
        >
          <Icon name="ri-add-circle-line" aria-hidden="true" />
          {t("trip.add_another_stop_to", { day: day.label })}
        </button>
        <TripDayBooking
          trip={trip}
          day={day}
          dayIndex={dayIndex}
          resolveItem={resolveItem}
          offerFor={offerFor}
          onChangeWhen={onChangeWhen}
        />
      </div>
    </article>
  );
}

// Keep the day date helper accessible to sibling files.
export { dayDate };
